import {
    Component,
    OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
    ActivatedRoute,
    RouterLink
} from '@angular/router';
import { MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Tag } from 'primeng/tag';
import {
    Tab,
    TabList,
    Tabs
} from 'primeng/tabs';

import { AuthService } from '@/auth/services';
import { AppLoaderComponent } from '@/components';
import { RateListingPreset } from '@/enums';
import { getCompetitorDisplayName } from '@/helpers';
import { CompetitorMapping } from '@/interfaces';
import { RateListingComponent } from '@/pages';
import { ValueOrPipe } from '@/pipes';
import { CompetitorMappingsService } from '@/service';

@Component({
    selector: 'app-competitor-details',
    templateUrl: 'view.component.html',
    styleUrls: ['./view.component.scss'],
    imports: [
        Button,
        RouterLink,
        AppLoaderComponent,
        ValueOrPipe,
        Tabs,
        TabList,
        Tab,
        RateListingComponent,
        FormsModule,
        Select,
        Tag,
    ],
    standalone: true
})
export class CompetitorViewComponent implements OnInit {
    loading = false;
    id!: string;
    competitor: CompetitorMapping | null = null;
    tierOptions: { label: string; value: number }[] = [];
    editingTierMode = false;
    editingTierValue: number | null = null;
    tabs = [
        { icon: 'pi pi-file', label: 'Documents', groupBy: 'source_file', preset: RateListingPreset.BY_SOURCE_FILE },
        { icon: 'pi pi-dollar', label: 'Rates' },
        { icon: 'pi pi-building', label: 'Districts', groupBy: 'district_mapping_id', preset: RateListingPreset.BY_DISTRICT }
    ];
    activeTab = 'Documents';

    constructor(
        public authService: AuthService,
        private service: CompetitorMappingsService,
        private route: ActivatedRoute,
        private messageService: MessageService
    ) {}

    get canEditTier(): boolean {
        return this.authService.hasPermission('competitor_mappings.competitor_mappings.update');
    }

    ngOnInit(): void {
        this.id = this.route.snapshot.params['competitor_id'];

        this.service.options().subscribe((options) => {
            this.tierOptions = options.tiers;
        });

        if (this.id) {
            this.view();
        }
    }

    view(): void {
        this.loading = true;
        this.service.view(this.id).subscribe({
            next: (data: CompetitorMapping) => {
                this.competitor = data;
                this.loading = false;
            },
            error: (err) => {
                this.loading = false;
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }

    openTierEditMode(): void {
        this.editingTierValue = this.competitor?.tier ?? null;
        this.editingTierMode = true;
    }

    cancelTierEdit(): void {
        this.editingTierMode = false;
        this.editingTierValue = null;
    }

    saveTier(): void {
        if (!this.competitor) {
            return;
        }

        this.loading = true;
        this.service.edit(this.id, { tier: this.editingTierValue }).subscribe({
            next: () => {
                this.competitor!.tier = this.editingTierValue;
                this.editingTierMode = false;
                this.loading = false;
                this.messageService.add({
                    severity: 'success',
                    detail: 'Competitor tier has been updated successfully.'
                });
            },
            error: (err) => {
                this.loading = false;
                if (err.error?.error) {
                    this.messageService.add({
                        severity: 'error',
                        detail: err.error?.error
                    });
                }
            }
        });
    }

    getActiveTab(): any {
        return this.tabs.find((e) => e.label === this.activeTab);
    }

    getDisplayAgencyName(): string | null {
        return this.competitor ? getCompetitorDisplayName(this.competitor) : null;
    }
}
